import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, X, FileText, TrendingDown, Package, Truck } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { fmtBRL, fmtKg, fmtNum } from "@/components/ui/Primitives";

const CATS = [
  { k: "cx1",  label: "CX1" },
  { k: "cx2",  label: "CX2" },
  { k: "cx3",  label: "CX3" },
  { k: "cx3g", label: "CX3G" },
  { k: "cx4",  label: "CX4" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${m}/${d.getFullYear()}`;
}

export default function RomaneioCustos() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: i }, { data: c }] = await Promise.all([
          api.get(`/invoices/${id}`),
          api.get(`/company`),
        ]);
        setInv(i); setCompany(c);
      } catch (e) { setErr(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const doPrint = () => window.print();

  if (loading) return <div className="p-8 text-center text-ink-500">Carregando…</div>;
  if (err) return <div className="p-8"><div className="text-danger">{err}</div></div>;
  if (!inv) return <div className="p-8">NF não encontrada.</div>;

  const benef = inv.beneficiamento || {};
  const custos = benef.custos || {};
  const isBalancao = benef.modo_precificacao === "balancao";
  const totalCaixas = Number(inv.total_caixas) || 0;
  const pesoLiquido = Number(inv.peso_liquido) || 0;
  const pesoBruto = Number(inv.peso_bruto) || 0;
  const quebraKg = Number(inv.quebra_kg) || 0;
  const quebraPct = pesoLiquido > 0 ? (quebraKg / pesoLiquido) * 100 : 0;

  // Composição
  const catsData = CATS.map((c) => {
    const b = benef[c.k] || {};
    const q = Number(b.quantidade || 0);
    const p = Number(b.preco || 0);
    return { ...c, quantidade: q, preco: p, subtotal: q * p, pct: totalCaixas > 0 ? (q / totalCaixas) * 100 : 0 };
  });

  // Valores calculados
  const valorBruto = Number(inv.valor_total_caixas || 0);
  const funrural = Number(inv.funrural || 0);
  const funruralPct = Number(custos.fun_rural_pct || 0);
  const liquidoProdutor = Number(inv.valor_liquido_fornecedor || 0);

  const frete = Number(inv.frete_total || 0);
  const fretePorTon = Number(custos.frete || 0);
  const chapa = Number(custos.chapa || 0);
  const icms = Number(custos.icms_seguro || 0);
  const custoMaqPorCx = Number(custos.custo_maquinacao || 0);
  const custoMaqTotal = Number(inv.custo_maquinacao_total || 0);
  const custoSacPorCx = Number(custos.sacaria || 0);
  const custoSacTotal = Number(inv.custo_sacaria_total || 0);
  const custosOperacionais = Number(inv.custos_operacionais || 0);

  const custoTotalCarga = liquidoProdutor + custosOperacionais;
  const custoMedioCx = totalCaixas > 0 ? custoTotalCarga / totalCaixas : 0;
  // Peso líquido REAL após beneficiamento = total sacos × 20 kg (peso padrão do saco)
  const pesoLiquidoReal = totalCaixas * 20;
  const custoMedioKg = pesoLiquidoReal > 0 ? custoTotalCarga / pesoLiquidoReal : 0;

  return (
    <div className="min-h-screen bg-stone-100 print:bg-white">
      {/* Toolbar */}
      <div className="print:hidden bg-white border-b border-ink-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => nav(-1)} className="p-2 hover:bg-stone-100 rounded flex items-center gap-2 text-sm" data-testid="close-romaneio-custos">
          <X size={16} /> Fechar
        </button>
        <div className="text-sm text-ink-700 font-semibold flex items-center gap-2">
          <TrendingDown size={16} className="text-amber-700" /> Romaneio de Custos — NF #{inv.nota_numero}
        </div>
        <button onClick={doPrint} className="px-4 py-2 rounded-md bg-amber-700 text-white text-sm font-semibold hover:opacity-90 flex items-center gap-2" data-testid="print-romaneio-custos-btn">
          <Printer size={16} /> Imprimir
        </button>
      </div>

      {/* Folha */}
      <div className="mx-auto my-6 print:my-0 bg-white shadow-lg print:shadow-none max-w-4xl print:max-w-none w-full">
        <div className="p-8 print:p-3 space-y-3 print:space-y-2 text-sm print:text-[10px]">
          {/* Cabeçalho */}
          <header className="border-b-2 border-amber-700 pb-2 print:pb-1.5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl print:text-lg font-bold text-ink-900 leading-tight uppercase tracking-tight">
                  {company?.razao_social || "Cebolas Serenata Ltda"}
                </h1>
                {company?.nome_fantasia && <div className="text-sm print:text-[9px] text-ink-500">{company.nome_fantasia}</div>}
                <div className="mt-1 text-xs print:text-[9px] text-ink-700">
                  <span className="text-ink-500">CNPJ:</span> <span className="font-mono">{company?.cnpj || "—"}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs print:text-[8px] uppercase tracking-widest text-amber-800 font-bold">Documento Interno</div>
                <div className="text-xl print:text-base font-mono font-bold text-amber-800">CUSTOS-{String(inv.romaneio_numero || 0).padStart(4, "0")}</div>
                <div className="text-xs print:text-[8px] text-ink-500 mt-1">Emitido em {fmtDate(new Date().toISOString())}</div>
              </div>
            </div>
          </header>

          {/* Título compacto */}
          <div className="text-center py-1 bg-amber-50 border border-amber-300 rounded">
            <h2 className="text-lg print:text-sm font-bold uppercase tracking-widest text-amber-900">Romaneio de Custos da Carga</h2>
            <div className="text-xs print:text-[8px] text-amber-800">USO INTERNO — não substitui o Romaneio do Produtor</div>
          </div>

          {/* Info NF */}
          <section className="grid grid-cols-2 gap-3 print:gap-2 border border-ink-300 rounded p-2 print:p-1.5">
            <div className="space-y-0.5">
              <div className="text-xs print:text-[8px] uppercase tracking-wider text-ink-500 font-bold mb-0.5">Nota Fiscal</div>
              <InfoRow label="Nº NF" value={`#${inv.nota_numero}`} mono bold />
              <InfoRow label="Data Entrada" value={fmtDate(inv.data_entrada)} mono />
              <InfoRow label="Data Beneficiamento" value={fmtDate(inv.data_beneficiamento)} mono />
              <InfoRow label="Tipo" value={<span className="capitalize">{inv.tipo_cebola}</span>} />
              <InfoRow label="Modo" value={isBalancao ? "Balanção" : "Boca da Máquina"} bold />
            </div>
            <div className="space-y-0.5">
              <div className="text-xs print:text-[8px] uppercase tracking-wider text-ink-500 font-bold mb-0.5">Produtor</div>
              <InfoRow label="Nome" value={inv.fornecedor || "—"} bold />
              <InfoRow label="Origem" value={inv.origem || "—"} />
              <InfoRow label="Motorista" value={inv.motorista || "—"} />
              <InfoRow label="Placa" value={inv.placa || "—"} mono />
            </div>
          </section>

          {/* Pesagens */}
          <section className="grid grid-cols-4 gap-2 print:gap-1">
            <PesagemCell label="Peso Bruto" value={fmtKg(pesoBruto)} />
            <PesagemCell label="Peso Tara" value={fmtKg(inv.peso_tara || 0)} />
            <PesagemCell label="Peso Líquido" value={fmtKg(pesoLiquido)} highlight />
            <PesagemCell label={`Quebra (${quebraPct.toFixed(1)}%)`} value={fmtKg(quebraKg)} danger={quebraPct > 8} />
          </section>

          {/* Composição — modo específico */}
          {isBalancao ? (
            <section className="border border-sky-300 rounded overflow-hidden">
              <div className="bg-sky-100 px-2 py-1 print:py-0.5 text-xs print:text-[9px] uppercase tracking-wider text-sky-900 font-bold border-b border-sky-300 flex items-center gap-1.5">
                <Package size={11} /> Pagamento no Balanção
              </div>
              <div className="p-2 print:p-1.5 grid grid-cols-4 gap-2 print:gap-1">
                <div>
                  <div className="text-[9px] print:text-[8px] uppercase text-ink-500 font-bold">Peso Líquido NF</div>
                  <div className="font-mono font-semibold text-xs print:text-[10px]">{fmtKg(pesoLiquido)}</div>
                </div>
                <div>
                  <div className="text-[9px] print:text-[8px] uppercase text-ink-500 font-bold">Desconto ({Number(benef.desconto_balancao_pct || 0)}%)</div>
                  <div className="font-mono font-semibold text-danger text-xs print:text-[10px]">-{fmtKg(pesoLiquido - Number(inv.peso_pagavel_balancao || pesoLiquido))}</div>
                </div>
                <div className="p-1 rounded bg-sky-50 border border-sky-300">
                  <div className="text-[9px] print:text-[8px] uppercase text-sky-800 font-bold">Peso Pagável</div>
                  <div className="font-mono font-bold text-sky-900 text-xs print:text-[10px]">{fmtKg(inv.peso_pagavel_balancao || pesoLiquido)}</div>
                </div>
                <div className="p-1 rounded bg-sky-50 border border-sky-300">
                  <div className="text-[9px] print:text-[8px] uppercase text-sky-800 font-bold">Preço acordado</div>
                  <div className="font-mono font-bold text-sky-900 text-xs print:text-[10px]">{fmtBRL(benef.preco_balancao || 0)}/kg</div>
                </div>
              </div>
              <div className="border-t border-sky-300 px-2 py-1.5 print:py-1 bg-sky-50 flex items-center justify-between">
                <span className="text-xs print:text-[10px] font-bold text-sky-900">Valor Bruto (peso pagável × preço/kg)</span>
                <span className="font-mono font-bold text-sky-900 text-base print:text-sm">{fmtBRL(valorBruto)}</span>
              </div>
              <div className="border-t border-sky-200 p-2 print:p-1 bg-white">
                <div className="text-[9px] print:text-[8px] uppercase tracking-wider text-ink-500 font-bold mb-1">Composição de saída (informativa)</div>
                <div className="grid grid-cols-5 gap-1 text-[10px] print:text-[9px]">
                  {catsData.map((c) => (
                    <div key={c.k} className="p-1 rounded bg-stone-50 border border-ink-200">
                      <div className="font-bold">{c.label}</div>
                      <div className="font-mono">{fmtNum(c.quantidade, 0)} sc</div>
                      <div className="text-[8px] text-ink-500">{c.pct.toFixed(1)}%</div>
                    </div>
                  ))}
                </div>
                <div className="mt-1 text-right text-[10px] print:text-[9px] font-bold text-ink-700">Total: {fmtNum(totalCaixas, 0)} sc</div>
              </div>
            </section>
          ) : (
            <section className="border border-ink-300 rounded overflow-hidden">
              <div className="bg-stone-100 px-2 py-1 print:py-0.5 text-xs print:text-[9px] uppercase tracking-wider text-ink-700 font-bold border-b border-ink-300 flex items-center gap-1.5">
                <Package size={11} /> Composição de Caixas — Pagamento por Categoria
              </div>
              <table className="w-full text-xs print:text-[10px]">
                <thead className="bg-stone-50 text-[10px] print:text-[9px] uppercase text-ink-600">
                  <tr>
                    <th className="text-left px-2 py-1">Categoria</th>
                    <th className="text-right px-2 py-1">Qtd (sc)</th>
                    <th className="text-right px-2 py-1">%</th>
                    <th className="text-right px-2 py-1">R$/sc</th>
                    <th className="text-right px-2 py-1">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {catsData.map((c) => (
                    <tr key={c.k} className={`border-t border-ink-200 ${c.quantidade === 0 ? "opacity-40" : ""}`}>
                      <td className="px-2 py-0.5"><span className="font-bold">{c.label}</span></td>
                      <td className="px-2 py-0.5 text-right font-mono">{fmtNum(c.quantidade, 0)}</td>
                      <td className="px-2 py-0.5 text-right font-mono text-ink-500">{c.pct.toFixed(1)}%</td>
                      <td className="px-2 py-0.5 text-right font-mono">{fmtBRL(c.preco)}</td>
                      <td className="px-2 py-0.5 text-right font-mono font-semibold">{fmtBRL(c.subtotal)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-ink-900 bg-stone-100 font-bold">
                    <td className="px-2 py-1">TOTAL</td>
                    <td className="px-2 py-1 text-right font-mono">{fmtNum(totalCaixas, 0)} sc</td>
                    <td className="px-2 py-1 text-right">—</td>
                    <td className="px-2 py-1 text-right">—</td>
                    <td className="px-2 py-1 text-right font-mono">{fmtBRL(valorBruto)}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          )}

          {/* Custos operacionais */}
          <section className="border border-amber-300 rounded overflow-hidden">
            <div className="bg-amber-100 px-2 py-1 print:py-0.5 text-xs print:text-[9px] uppercase tracking-wider text-amber-900 font-bold border-b border-amber-300 flex items-center gap-1.5">
              <Truck size={11} /> Custos Operacionais da Empresa
            </div>
            <table className="w-full text-xs print:text-[10px]">
              <tbody>
                <tr className="border-b border-ink-200">
                  <td className="px-2 py-0.5">Frete</td>
                  <td className="px-2 py-0.5 text-ink-500 text-[10px] print:text-[9px]">{fmtBRL(fretePorTon)}/t × {(pesoLiquido/1000).toFixed(3)}t líq.</td>
                  <td className="px-2 py-0.5 text-right font-mono">{fmtBRL(frete)}</td>
                </tr>
                <tr className="border-b border-ink-200">
                  <td className="px-2 py-0.5">Chapa</td>
                  <td className="px-2 py-0.5 text-ink-500 text-[10px] print:text-[9px]">valor fixo</td>
                  <td className="px-2 py-0.5 text-right font-mono">{fmtBRL(chapa)}</td>
                </tr>
                <tr className="border-b border-ink-200">
                  <td className="px-2 py-0.5">ICMS / Seguro</td>
                  <td className="px-2 py-0.5 text-ink-500 text-[10px] print:text-[9px]">valor fixo</td>
                  <td className="px-2 py-0.5 text-right font-mono">{fmtBRL(icms)}</td>
                </tr>
                <tr className="border-b border-ink-200">
                  <td className="px-2 py-0.5">Maquinação</td>
                  <td className="px-2 py-0.5 text-ink-500 text-[10px] print:text-[9px]">{fmtBRL(custoMaqPorCx)}/sc × {fmtNum(totalCaixas, 0)} sc</td>
                  <td className="px-2 py-0.5 text-right font-mono">{fmtBRL(custoMaqTotal)}</td>
                </tr>
                <tr className="border-b border-ink-200">
                  <td className="px-2 py-0.5">Sacaria</td>
                  <td className="px-2 py-0.5 text-ink-500 text-[10px] print:text-[9px]">{fmtBRL(custoSacPorCx)}/sc × {fmtNum(totalCaixas, 0)} sc</td>
                  <td className="px-2 py-0.5 text-right font-mono">{fmtBRL(custoSacTotal)}</td>
                </tr>
                <tr className="border-t-2 border-amber-700 bg-amber-50 font-bold">
                  <td className="px-2 py-1" colSpan={2}>TOTAL CUSTOS OPERACIONAIS</td>
                  <td className="px-2 py-1 text-right font-mono text-amber-900">{fmtBRL(custosOperacionais)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Consolidado */}
          <section className="grid grid-cols-2 gap-2 print:gap-1.5">
            <div className="border border-emerald-300 rounded p-2 print:p-1.5 bg-emerald-50">
              <div className="text-xs print:text-[9px] uppercase tracking-wider text-emerald-800 font-bold mb-1">Pagamento ao Produtor</div>
              <table className="w-full text-xs print:text-[10px]">
                <tbody>
                  <tr>
                    <td className="text-ink-600">Valor bruto</td>
                    <td className="text-right font-mono">{fmtBRL(valorBruto)}</td>
                  </tr>
                  <tr>
                    <td className="text-ink-600">Funrural ({funruralPct}%)</td>
                    <td className="text-right font-mono text-danger">-{fmtBRL(funrural)}</td>
                  </tr>
                  <tr className="border-t border-emerald-300 font-bold">
                    <td className="pt-1 text-emerald-900">Líquido produtor</td>
                    <td className="pt-1 text-right font-mono text-emerald-900">{fmtBRL(liquidoProdutor)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="border border-ink-800 rounded p-2 print:p-1.5 bg-ink-900 text-white">
              <div className="text-xs print:text-[9px] uppercase tracking-wider text-amber-300 font-bold mb-1">Custo Total da Carga</div>
              <table className="w-full text-xs print:text-[10px]">
                <tbody>
                  <tr>
                    <td className="text-stone-300">Pgto produtor</td>
                    <td className="text-right font-mono">{fmtBRL(liquidoProdutor)}</td>
                  </tr>
                  <tr>
                    <td className="text-stone-300">Custos operacionais</td>
                    <td className="text-right font-mono">{fmtBRL(custosOperacionais)}</td>
                  </tr>
                  <tr className="border-t border-stone-600 font-bold">
                    <td className="pt-1 text-amber-300 text-sm print:text-xs">CUSTO TOTAL</td>
                    <td className="pt-1 text-right font-mono text-amber-300 text-sm print:text-xs">{fmtBRL(custoTotalCarga)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Preço médio real */}
          <section className="border border-ink-300 rounded p-2 print:p-1.5 bg-stone-50">
            <div className="text-xs print:text-[9px] uppercase tracking-wider text-ink-700 font-bold mb-1">Custo Médio (Real)</div>
            <div className="grid grid-cols-2 gap-3 text-xs print:text-[10px]">
              <div>
                <div className="text-ink-500 text-[10px] print:text-[9px]">Custo Total ÷ Sacos</div>
                <div className="font-mono font-bold text-base print:text-sm text-ink-900">{fmtBRL(custoMedioCx)} <span className="text-xs font-normal text-ink-500">/sc</span></div>
              </div>
              <div>
                <div className="text-ink-500 text-[10px] print:text-[9px]">Custo Total ÷ Peso Líquido Real</div>
                <div className="font-mono font-bold text-base print:text-sm text-ink-900">{fmtBRL(custoMedioKg)} <span className="text-xs font-normal text-ink-500">/kg</span></div>
                <div className="text-[9px] print:text-[8px] text-ink-500">{fmtNum(totalCaixas, 0)} sc × 20 kg = {fmtKg(pesoLiquidoReal)}</div>
              </div>
            </div>
            <div className="mt-1 text-[9px] print:text-[8px] text-ink-500">
              * Inclui o líquido pago ao produtor (após Funrural) + todos os custos operacionais da empresa.
              Diferente do &quot;preço médio&quot; do romaneio do produtor, que reflete apenas o valor bruto pago.
            </div>
          </section>

          <footer className="text-center text-[9px] print:text-[8px] text-ink-500 pt-1 border-t border-ink-200">
            Documento interno de custos · Impresso em {fmtDate(new Date().toISOString())} · Não é documento fiscal
          </footer>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 6mm; }
          body { margin: 0; }
          html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function InfoRow({ label, value, mono, bold }) {
  return (
    <div className="flex text-xs print:text-[10px]">
      <span className="text-ink-500 w-32 print:w-28 flex-shrink-0">{label}:</span>
      <span className={`${mono ? "font-mono" : ""} ${bold ? "font-bold text-ink-900" : "text-ink-700"} truncate`}>{value}</span>
    </div>
  );
}

function PesagemCell({ label, value, highlight, danger }) {
  return (
    <div className={`p-1.5 print:p-1 rounded ${highlight ? "bg-emerald-50 border border-emerald-300" : danger ? "bg-red-50 border border-red-300" : "bg-stone-50 border border-ink-200"}`}>
      <div className="text-[9px] print:text-[8px] uppercase tracking-wider text-ink-500 font-bold">{label}</div>
      <div className={`font-mono font-semibold text-xs print:text-[11px] ${highlight ? "text-emerald-700" : danger ? "text-danger" : "text-ink-900"}`}>{value}</div>
    </div>
  );
}
